// The RETURNING-VISITOR mark: two arrows chasing each other around a bubble, like the loop in a "returning visitor" icon with the bubble as the person.
//
// It is DRAWN BY CODE (no icon file): one small SVG, generated here from the bubble's radius and colour, used as the href of an <image> that sits
// on the bubble like the unidentified badge does. Pure on purpose: no DOM, no d3, never throws, so the geometry is testable and the same drawing
// can be reused anywhere else a returning visitor is shown (the Sessions row glyph).
//
// THE COLOUR. History, because it changed twice: the first version drew the arrows in the bubble's own category colour; Chris then asked for black
// ("Black since that is not used in any bubble color", 2026-10-12) so it could never be mistaken for a category; on production the same day he reversed it
// for the DOT-mode orbit ("the fact that it is black is not helping. Can we keep it the same color as the bubble, just make the circle arrows further
// out"). So: DOT mode = the bubble's own colour, passed in by the caller (returnMarkFor); ICON mode = this black badge, which has no bubble colour to take
// (the bubble is an icon) and keeps its white outline. RETURN_COLOR is that black, and the fallback when no colour is known.
export const RETURN_COLOR = "#000000";

// THE NUMBERS, in pixels at the default dot bubble (radius 4), and they scale with the radius so a converted visitor's larger bubble (radius x1.6)
// keeps the same proportions. The first version was the mock Chris approved ("E1", 2026-10-11): orbit 5.3px, stroke 1px, arrowhead 2.2 x 3px. On
// production (2026-10-12) it was "hard to tell it is the return visitor": a 1px line hugging a dimmed dot, half hidden by the away badge. Now:
//   bubble edge 4px | orbit 7.5px | stroke 1.5px | arrowhead 3.0px long, 4px wide | white underlay 1.4px wider than the stroke
// Samples at 5.8, 7.5, 9.0 and 10.5 were rendered in the real engine; Chris chose 7.5 at stroke 1.5. At 7.5 the arcs overlap the transient hover ring (its solid
// line spans 6.6-8.6px) and the selection ripple (starts at 7.6px) and the away badge, which is why every glyph is drawn ABOVE the arrows (see appendReturnMark's
// layering note). ORBIT is the one number to change.

export const RETURN_MARK = {
  BASE_RADIUS: 4, // the dot bubble the numbers below are drawn for
  ORBIT: 7.5,
  STROKE: 1.5,
  HEAD_LENGTH: 3.0,
  HEAD_HALF_WIDTH: 2.0,
  // a white drawing a little wider than the black one, UNDER it: the arrows read over any dot colour, a neighbouring bubble or a badge
  HALO: 1.4,
  HALO_COLOR: "#ffffff",
  // Two arcs chasing each other clockwise, each ending in an arrowhead. Angles in degrees, 0 = to the right, clockwise on screen.
  ARCS: [
    [215, 335],
    [35, 155],
  ] as Array<[number, number]>,
};

const n2 = (n: number) => n.toFixed(2);

const pointAt = (r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [r * Math.cos(a), r * Math.sin(a)];
};

const arcPath = (r: number, from: number, to: number) => {
  const [x0, y0] = pointAt(r, from);
  const [x1, y1] = pointAt(r, to);
  const large = (((to - from) % 360) + 360) % 360 > 180 ? 1 : 0;
  return `M${n2(x0)} ${n2(y0)} A${n2(r)} ${n2(r)} 0 ${large} 1 ${n2(x1)} ${n2(y1)}`;
};

// A triangle whose tip points the way the arc is travelling (clockwise) at `deg`.
const headPath = (r: number, deg: number, length: number, half: number) => {
  const [px, py] = pointAt(r, deg);
  const a = (deg * Math.PI) / 180;
  const tangent: [number, number] = [-Math.sin(a), Math.cos(a)];
  const normal: [number, number] = [Math.cos(a), Math.sin(a)];
  const tip = [px + tangent[0] * length, py + tangent[1] * length];
  const left = [px + normal[0] * half, py + normal[1] * half];
  const right = [px - normal[0] * half, py - normal[1] * half];
  return `M${n2(tip[0])} ${n2(tip[1])} L${n2(left[0])} ${n2(left[1])} L${n2(right[0])} ${n2(right[1])} Z`;
};

export interface ReturnMark {
  uri: string; // data: URI for an <image href>
  size: number; // the image's width and height in px, centred on the bubble
}

// The SVG's own numbers for a bubble of this radius. Kept separate from the string so tests can read them.
//
// `markRadius` CAPS how big the drawing itself gets (stroke, arrowheads, the gap to the dot): it is the radius the drawing is sized for, never more than the
// bubble's. The ring still sits just outside the BUBBLE (orbit = ORBIT * scale + the part of the bubble beyond the capped size). It exists for the zoomed
// view: at 25x a dot is 300px across, and arrows that scale with it overflowed the card and were clipped at the top and bottom. Left out, the mark scales
// with the bubble exactly as before.
export const returnMarkMetrics = (bubbleRadius: number, markRadius: number = bubbleRadius) => {
  const radius = Number.isFinite(bubbleRadius) && bubbleRadius > 0 ? bubbleRadius : RETURN_MARK.BASE_RADIUS;
  const sized = Number.isFinite(markRadius) && markRadius > 0 ? Math.min(markRadius, radius) : radius;
  const scale = sized / RETURN_MARK.BASE_RADIUS;
  const orbit = RETURN_MARK.ORBIT * scale + (radius - sized);
  const stroke = RETURN_MARK.STROKE * scale;
  const headLength = RETURN_MARK.HEAD_LENGTH * scale;
  const headHalf = RETURN_MARK.HEAD_HALF_WIDTH * scale;
  const halo = RETURN_MARK.HALO * scale;
  // the furthest any part of the drawing reaches from the centre (an arrowhead's outer corner, or an arc plus half its stroke), plus the white underlay
  const extent = Math.max(orbit + headHalf, orbit + stroke / 2, Math.hypot(orbit, headLength)) + stroke / 2 + halo / 2;
  return { scale, orbit, stroke, headLength, headHalf, halo, extent, size: extent * 2 };
};

// Colours reach the SVG through an attribute, so anything that is not a plain colour is refused rather than injected.
const SAFE_COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\([0-9.,\s%]+\)|[a-zA-Z]+)$/;

const cache = new Map<string, ReturnMark>();

export const buildReturnMark = (color: string, bubbleRadius: number, markRadius: number = bubbleRadius): ReturnMark => {
  const safeColor = typeof color === "string" && SAFE_COLOR.test(color.trim()) ? color.trim() : "#444441";
  const m = returnMarkMetrics(bubbleRadius, markRadius);
  const key = `${safeColor}|${m.size.toFixed(2)}|${m.orbit.toFixed(2)}`;
  const hit = cache.get(key);
  if (hit) {
    return hit;
  }
  const arcs = RETURN_MARK.ARCS.map(([from, to]) => `<path d="${arcPath(m.orbit, from, to)}"/>`).join("");
  const heads = RETURN_MARK.ARCS.map(([, to]) => `<path d="${headPath(m.orbit, to, m.headLength, m.headHalf)}"/>`).join("");
  const e = n2(m.extent);
  const s = n2(m.size);
  const halo = RETURN_MARK.HALO_COLOR;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="-${e} -${e} ${s} ${s}">` +
    // the white underlay first (wider strokes), then the drawing on top of it
    (m.halo > 0
      ? `<g fill="none" stroke="${halo}" stroke-width="${n2(m.stroke + m.halo)}" stroke-linecap="round">${arcs}</g>` +
        `<g fill="${halo}" stroke="${halo}" stroke-width="${n2(m.halo)}" stroke-linejoin="round">${heads}</g>`
      : "") +
    `<g fill="none" stroke="${safeColor}" stroke-width="${n2(m.stroke)}" stroke-linecap="round">${arcs}</g>` +
    `<g fill="${safeColor}" stroke="${safeColor}" stroke-width="${n2(0.25 * m.scale)}" stroke-linejoin="round">${heads}</g>` +
    "</svg>";
  const mark = { uri: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, size: m.size };
  cache.set(key, mark);
  return mark;
};

// ICON MODE (2026-10-11): a bubble is a 19px icon (flag, OS, browser...), and a circle that clears its corners is wider than the app's hover and
// ripple ring (13.4px vs 11.05px), so the orbit above cannot be used. Instead the SAME two arrows are drawn as a small badge pinned to the
// icon's top-right corner (top-left is the away glyph, bottom-left the unidentified badge, bottom-right the goal star), with NO background disc:
// Chris rejected the dark disc ("just the arrows in a circle"). With nothing behind them they must read over any flag or icon colour, so they
// are drawn BLACK with a white outline. Black because no bubble category colour is black (the palette's darkest is #14477d), so it can never be
// mistaken for a category.
export const RETURN_BADGE = {
  BASE_RADIUS: 7, // the icon-mode bubble radius these numbers are drawn for (a 19px icon)
  ORBIT: 4.4,
  STROKE: 1.2,
  OUTLINE: 1.6, // extra width of the white outline, over the stroke
  HEAD_LENGTH: 2.1,
  HEAD_HALF_WIDTH: 1.6,
  OFFSET: 6.8, // the badge's centre, up and to the right of the icon's centre
  COLOR: RETURN_COLOR,
  OUTLINE_COLOR: "#ffffff",
};

// `markRadius` caps the badge's own size (see returnMarkMetrics); its POSITION, the icon's top-right corner, still follows the bubble's radius.
export const returnBadgeMetrics = (bubbleRadius: number, markRadius: number = bubbleRadius) => {
  const radius = Number.isFinite(bubbleRadius) && bubbleRadius > 0 ? bubbleRadius : RETURN_BADGE.BASE_RADIUS;
  const sized = Number.isFinite(markRadius) && markRadius > 0 ? Math.min(markRadius, radius) : radius;
  const scale = sized / RETURN_BADGE.BASE_RADIUS;
  const orbit = RETURN_BADGE.ORBIT * scale;
  const stroke = RETURN_BADGE.STROKE * scale;
  const outline = RETURN_BADGE.OUTLINE * scale;
  const headLength = RETURN_BADGE.HEAD_LENGTH * scale;
  const headHalf = RETURN_BADGE.HEAD_HALF_WIDTH * scale;
  const reach = outline / 2; // how far the white outline stands out past each shape
  const extent = Math.max(orbit + headHalf + reach, orbit + (stroke + outline) / 2, Math.hypot(orbit, headLength) + reach);
  return { scale, orbit, stroke, outline, headLength, headHalf, extent, size: extent * 2, offset: RETURN_BADGE.OFFSET * (radius / RETURN_BADGE.BASE_RADIUS) };
};

export interface ReturnBadge extends ReturnMark {
  offset: number; // up-and-right of the icon's centre (the same distance on both axes)
}

const badgeCache = new Map<string, ReturnBadge>();

export const buildReturnBadge = (bubbleRadius: number, markRadius: number = bubbleRadius): ReturnBadge => {
  const m = returnBadgeMetrics(bubbleRadius, markRadius);
  const key = `${m.size.toFixed(2)}|${m.offset.toFixed(2)}`;
  const hit = badgeCache.get(key);
  if (hit) {
    return hit;
  }
  const arcs = RETURN_MARK.ARCS.map(([from, to]) => arcPath(m.orbit, from, to));
  const heads = RETURN_MARK.ARCS.map(([, to]) => headPath(m.orbit, to, m.headLength, m.headHalf));
  const group = (paths: string[], attrs: string) => `<g ${attrs}>${paths.map((d) => `<path d="${d}"/>`).join("")}</g>`;
  const fill = RETURN_BADGE.COLOR;
  const halo = RETURN_BADGE.OUTLINE_COLOR;
  const e = n2(m.extent);
  const s = n2(m.size);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="-${e} -${e} ${s} ${s}">` +
    // white outline first (the wider strokes), then the black drawing on top of it
    group(arcs, `fill="none" stroke="${halo}" stroke-width="${n2(m.stroke + m.outline)}" stroke-linecap="round"`) +
    group(heads, `fill="${halo}" stroke="${halo}" stroke-width="${n2(m.outline)}" stroke-linejoin="round"`) +
    group(arcs, `fill="none" stroke="${fill}" stroke-width="${n2(m.stroke)}" stroke-linecap="round"`) +
    group(heads, `fill="${fill}" stroke="${fill}" stroke-width="${n2(0.3 * m.scale)}" stroke-linejoin="round"`) +
    "</svg>";
  const badge = { uri: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, size: m.size, offset: m.offset };
  badgeCache.set(key, badge);
  return badge;
};

// THE SESSIONS-ROW GLYPH (2026-10-12). Chris: the colour-per-row dot version "doesn't make sense, make them all the same glyph", and he chose THIS one:
// the black arrows in a circle with no dot and no background, i.e. exactly the icon-mode badge above. So the row uses that very drawing, scaled so the
// whole image is `sizePx` square (the row's mark slots are 13px). Every returning row gets the identical image: it does not depend on the row.
export const returnGlyphRadius = (sizePx: number): number => {
  const unit = returnBadgeMetrics(RETURN_BADGE.BASE_RADIUS).size / RETURN_BADGE.BASE_RADIUS; // image px per bubble-radius px
  const radius = Number.isFinite(sizePx) && sizePx > 0 ? sizePx / unit : RETURN_BADGE.BASE_RADIUS;

  return radius;
};

export const buildReturnGlyph = (sizePx: number): ReturnMark => {
  const { uri, size } = buildReturnBadge(returnGlyphRadius(sizePx));

  return { uri, size };
};

// WHAT TO DRAW FOR A RETURNING VISITOR, in ONE place for the grid (clusterService's appendReturnMark) and the zoomed view (densityCloudService's drawBadges),
// so the two cannot drift apart: a plain dot gets the arrows orbiting it in the dot's own colour; an icon gets the black corner badge on its top-right.
// `radius` is the bubble's radius in the units the caller draws in (world units for the cloud); the offsets are from the bubble's centre, in the same units.
export interface ReturnMarkFor extends ReturnMark {
  offsetX: number;
  offsetY: number;
}

// `markRadius` (optional) caps the DRAWING's size while the bubble keeps its own: the zoomed view passes it so the arrows stop growing with the dot (see returnMarkMetrics).
export const returnMarkFor = (asIcon: boolean, radius: number, dotColor?: string | null, markRadius: number = radius): ReturnMarkFor => {
  if (asIcon) {
    const badge = buildReturnBadge(radius, markRadius);

    return { uri: badge.uri, size: badge.size, offsetX: badge.offset, offsetY: -badge.offset };
  }
  const mark = buildReturnMark(typeof dotColor === "string" && dotColor.length > 0 ? dotColor : RETURN_COLOR, radius, markRadius);

  return { uri: mark.uri, size: mark.size, offsetX: 0, offsetY: 0 };
};
